import {
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  Clock,
  RotateCcw,
  Zap,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

const QuickActionsDropdown = ({
  onDateSelect,
  currentStartDate,
  currentEndDate,
  isCustomMode,
}) => {
  const { t } = useTranslation("common");
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const formatDate = (date) => {
    return date.toLocaleDateString("en-CA"); // YYYY-MM-DD format
  };

  const handleQuickAction = (action) => {
    const today = new Date();
    let startDate, endDate;

    switch (action) {
      case "today":
        startDate = new Date(today);
        endDate = new Date(today);
        break;
      case "yesterday":
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 1);
        endDate = new Date(startDate);
        break;
      case "thisMonth":
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = new Date(today);
        break;
      case "lastMonth":
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        endDate = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case "thisYear":
        startDate = new Date(today.getFullYear(), 0, 1);
        endDate = new Date(today);
        break;
      case "moveMonthForward":
        if (currentStartDate && currentEndDate) {
          startDate = new Date(currentStartDate);
          endDate = new Date(currentEndDate);
          startDate.setMonth(startDate.getMonth() + 1);
          endDate.setMonth(endDate.getMonth() + 1);
        } else {
          return;
        }
        break;
      case "moveMonthBackward":
        if (currentStartDate && currentEndDate) {
          startDate = new Date(currentStartDate);
          endDate = new Date(currentEndDate);
          startDate.setMonth(startDate.getMonth() - 1);
          endDate.setMonth(endDate.getMonth() - 1);
        } else {
          return;
        }
        break;
      default:
        return;
    }

    if (onDateSelect) {
      onDateSelect(formatDate(startDate), formatDate(endDate));
    }
    handleClose();
  };

  return (
    <>
      <Tooltip title={t("header.quickActions.title")}>
        <IconButton
          onClick={handleClick}
          size="small"
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "4px",
            padding: "8px",
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          <Zap size={20} />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            minWidth: 200,
            mt: 1,
          },
        }}
      >
        <MenuItem onClick={() => handleQuickAction("today")}>
          <ListItemIcon>
            <Calendar size={18} />
          </ListItemIcon>
          <ListItemText primary={t("header.quickActions.today")} />
        </MenuItem>

        <MenuItem onClick={() => handleQuickAction("yesterday")}>
          <ListItemIcon>
            <Clock size={18} />
          </ListItemIcon>
          <ListItemText primary={t("header.quickActions.yesterday")} />
        </MenuItem>

        <Divider />

        <MenuItem onClick={() => handleQuickAction("thisMonth")}>
          <ListItemIcon>
            <RotateCcw size={18} />
          </ListItemIcon>
          <ListItemText primary={t("header.quickActions.thisMonth")} />
        </MenuItem>

        <MenuItem onClick={() => handleQuickAction("lastMonth")}>
          <ListItemIcon>
            <RotateCcw size={18} />
          </ListItemIcon>
          <ListItemText primary={t("header.quickActions.lastMonth")} />
        </MenuItem>

        <MenuItem onClick={() => handleQuickAction("thisYear")}>
          <ListItemIcon>
            <RotateCcw size={18} />
          </ListItemIcon>
          <ListItemText primary={t("header.quickActions.thisYear")} />
        </MenuItem>

        <Divider />

        <MenuItem
          onClick={() => handleQuickAction("moveMonthBackward")}
          disabled={!isCustomMode || !currentStartDate || !currentEndDate}
        >
          <ListItemIcon>
            <ArrowLeft size={18} />
          </ListItemIcon>
          <ListItemText primary={t("header.quickActions.moveMonthBackward")} />
        </MenuItem>

        <MenuItem
          onClick={() => handleQuickAction("moveMonthForward")}
          disabled={!isCustomMode || !currentStartDate || !currentEndDate}
        >
          <ListItemIcon>
            <ArrowRight size={18} />
          </ListItemIcon>
          <ListItemText primary={t("header.quickActions.moveMonthForward")} />
        </MenuItem>
      </Menu>
    </>
  );
};

export default QuickActionsDropdown;
